import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment/moment";
import { toast } from "react-toastify";
import { GuideInvoice } from "./interfaces";
import { downloadGuideInvoiceFile } from "./fetchGuideInvoiceFile";

const Row = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid #eee;
    border-radius: 8px;
    background: #fafafa;
`;

const Info = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
`;

const FileName = styled.span`
    font-weight: 500;
    color: #333;
    word-break: break-word;
`;

const Meta = styled.span`
    font-size: 0.85rem;
    color: #666;
`;

const Actions = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
`;

const Button = styled.button`
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const DownloadButton = styled(Button)`
    background: #1890ff;
    color: white;

    &:hover:not(:disabled) {
        background: #096dd9;
    }
`;

const DeleteButton = styled(Button)`
    background: #dc3545;
    color: white;

    &:hover:not(:disabled) {
        background: #c82333;
    }
`;

interface GuideInvoiceRowProps {
    invoice: GuideInvoice;
    onDelete?: (invoiceId: string) => void;
}

const GuideInvoiceRow: React.FC<GuideInvoiceRowProps> = ({ invoice, onDelete }) => {
    const [downloading, setDownloading] = useState(false);

    const displayName =
        invoice.originalFileName || `Invoice ${invoice.month}/${invoice.year}`;

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await downloadGuideInvoiceFile(invoice._id, invoice.originalFileName);
        } catch {
            toast.error("Failed to download invoice");
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = () => {
        if (!onDelete) return;
        if (!window.confirm("Are you sure you want to delete this invoice?")) {
            return;
        }
        onDelete(invoice._id);
    };

    return (
        <Row>
            <Info>
                <FileName>{displayName}</FileName>
                <Meta>
                    Added {moment(invoice.createdAt).format("D MMM YYYY h:mm a")}
                </Meta>
            </Info>
            <Actions>
                <DownloadButton
                    type="button"
                    onClick={handleDownload}
                    disabled={downloading}
                >
                    {downloading ? "Downloading..." : "Download"}
                </DownloadButton>
                {onDelete && (
                    <DeleteButton type="button" onClick={handleDelete}>
                        Delete
                    </DeleteButton>
                )}
            </Actions>
        </Row>
    );
};

export default GuideInvoiceRow;
